import { FileText, Loader2, Upload } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mediaUrl, type ApiError } from '@/core/api';
import { formatBytes } from '@/lib/format';
import { uploadLessonAsset } from '../api';
import type { AssetUploader } from '../lib/blocks';
import { ASSET_ACCEPT } from '../lib/blocks';
import type { AssetBlockData, AssetBlockType } from '../types';

function AssetPreview({ kind, data }: { kind: AssetBlockType; data: AssetBlockData }) {
  const { i18n } = useTranslation();
  const src = mediaUrl(data.url ?? '');
  if (kind === 'image') {
    return (
      <img
        src={src}
        alt={data.caption || data.filename || ''}
        className="max-h-72 max-w-full rounded-md border object-contain"
      />
    );
  }
  if (kind === 'video') {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption -- teacher uploads carry no caption tracks; the optional text caption renders beside the player
      <video controls preload="metadata" src={src} className="max-h-72 w-full rounded-md border" />
    );
  }
  if (kind === 'audio') {
    // eslint-disable-next-line jsx-a11y/media-has-caption -- teacher uploads carry no caption tracks; the optional text caption renders beside the player
    return <audio controls preload="metadata" src={src} className="w-full" />;
  }
  return (
    <p className="bg-muted/60 text-foreground flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
      <FileText className="text-muted-foreground size-4 shrink-0" />
      <span className="truncate font-medium">{data.filename}</span>
      {typeof data.size === 'number' ? (
        <span className="text-muted-foreground shrink-0">
          {formatBytes(data.size, i18n.language)}
        </span>
      ) : null}
    </p>
  );
}

interface AssetBlockEditorProps {
  kind: AssetBlockType;
  data: AssetBlockData;
  onChange: (data: AssetBlockData) => void;
  /** Upload target — lesson assets by default, homework assets when the
   *  homework editor reuses this component (ADR-014's mirror table). */
  upload?: AssetUploader;
}

/**
 * Editor body for image/video/audio/file blocks: upload (with progress and
 * server-side size/type errors surfaced), preview after upload, and the
 * caption (media) / label (file) text.
 */
export function AssetBlockEditor({
  kind,
  data,
  onChange,
  upload = uploadLessonAsset,
}: AssetBlockEditorProps) {
  const { t } = useTranslation(['curriculum']);
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploading = progress !== null;
  const textKey = kind === 'file' ? 'label' : 'caption';

  async function onFileSelected(file: File | undefined) {
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      const asset = await upload(kind, file, setProgress);
      onChange({
        ...data,
        asset: asset.public_id,
        // Keep the read-enrichment fields locally for instant preview; they
        // are stripped again before saving (serializeBlocks).
        url: asset.url,
        filename: asset.original_filename,
        size: asset.size,
        content_type: asset.content_type,
      });
    } catch (e) {
      const err = e as ApiError;
      setError(
        err?.fieldErrors?.file?.join(' ') ?? err?.fieldErrors?.kind?.join(' ') ?? err?.message
      );
    } finally {
      setProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-3">
      {data.asset && data.url ? <AssetPreview kind={kind} data={data} /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept={ASSET_ACCEPT[kind]}
          className="sr-only"
          disabled={uploading}
          onChange={e => void onFileSelected(e.target.files?.[0])}
        />
        <Button asChild variant="outline" size="sm">
          <label htmlFor={inputId} className="cursor-pointer">
            {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
            {uploading
              ? t('curriculum:editor.asset.uploading', { percent: progress })
              : data.asset
                ? t('curriculum:editor.asset.replace')
                : t('curriculum:editor.asset.upload')}
          </label>
        </Button>
        {!data.asset && !uploading ? (
          <span className="text-muted-foreground text-xs">
            {t(`curriculum:editor.asset.hint.${kind}`)}
          </span>
        ) : null}
      </div>
      {uploading ? (
        <div
          className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
          role="progressbar"
          aria-valuenow={progress ?? 0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('curriculum:editor.asset.progressLabel')}
        >
          <div className="bg-primary h-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      ) : null}
      {error ? <p className="text-destructive text-sm">{error}</p> : null}

      {data.asset ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${inputId}-text`}>{t(`curriculum:editor.asset.${textKey}`)}</Label>
          <Input
            id={`${inputId}-text`}
            value={(kind === 'file' ? data.label : data.caption) ?? ''}
            maxLength={500}
            onChange={e => onChange({ ...data, [textKey]: e.target.value })}
          />
        </div>
      ) : null}
    </div>
  );
}
